<script lang="ts" setup>
import { countries } from '@/models/country.model';
import { Address } from '@/models/info.model';
import { PropType, ref, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: Object as PropType<Address>,
    required: true,
  },
  label: {
    type: String,
  },
  removable: {
    type: Boolean,
  },
  disabled: {
    type: Boolean,
  },
});

const address = ref<Address>({ ...props.modelValue });

const emit = defineEmits(['update:modelValue', 'change', 'remove']);

function update() {
  emit('update:modelValue', address.value);
  emit('change', address.value);
}

function remove() {
  emit('remove');
}

watch(
  () => props.modelValue,
  () => {
    address.value = { ...props.modelValue };
  },
);
</script>
<template>
  <v-row dense v-if="label">
    <v-col
      ><h4>{{ label }}</h4></v-col
    >
  </v-row>
  <v-row dense>
    <v-col :cols="removable ? 3 : 4">
      <v-text-field
        v-model="address.name"
        @change="update"
        :label="$t('initiative.edit.name')"
        density="compact"
        :disabled="disabled"
        :hide-details="true"
      ></v-text-field>
    </v-col>
    <v-col cols="5">
      <v-text-field
        v-model="address.street"
        @change="update"
        :label="$t('initiative.edit.street')"
        density="compact"
        :disabled="disabled"
        :hide-details="true"
      ></v-text-field>
    </v-col>
    <v-col cols="3">
      <v-text-field
        v-model="address.house_number"
        @change="update"
        :label="$t('initiative.edit.housenumber')"
        density="compact"
        :disabled="disabled"
        :hide-details="true"
      ></v-text-field>
    </v-col>
    <v-col cols="1" v-if="removable">
      <v-btn
        icon="mdi-delete-outline"
        class="remove-button mt-2"
        density="compact"
        @click.prevent="remove"
      ></v-btn>
    </v-col>
  </v-row>
  <v-row dense>
    <v-col cols="6">
      <v-text-field
        v-model="address.address_extra"
        @change="update"
        :label="$t('initiative.edit.extra')"
        density="compact"
        :disabled="disabled"
        :hide-details="true"
      ></v-text-field>
    </v-col>
    <v-col cols="6">
      <v-text-field
        v-model="address.neighbourhood"
        @change="update"
        density="compact"
        :label="$t('initiative.edit.neighbourhood')"
        :disabled="disabled"
        :hide-details="true"
      ></v-text-field>
    </v-col>
  </v-row>
  <v-row dense>
    <v-col cols="4">
      <v-text-field
        v-model="address.postal_code"
        @change="update"
        :label="$t('initiative.edit.postalcode')"
        density="compact"
        :disabled="disabled"
        :hide-details="true"
      ></v-text-field>
    </v-col>
    <v-col cols="8">
      <v-text-field
        v-model="address.city"
        @change="update"
        :label="$t('initiative.edit.city')"
        density="compact"
        :disabled="disabled"
        :hide-details="true"
      ></v-text-field>
    </v-col>
  </v-row>
  <v-row dense>
    <v-col cols="6">
      <v-autocomplete
        v-model="address.country"
        @change="update"
        :items="countries"
        density="compact"
        :label="$t('initiative.edit.country')"
        item-title="text"
        item-value="value"
        :disabled="disabled"
        :hide-details="true"
      ></v-autocomplete>
    </v-col>
    <v-col cols="3">
      <v-checkbox
        :label="$t('initiative.edit.exact')"
        density="compact"
        v-model="address.isSpecific"
        :disabled="disabled"
        @change="update"
      />
    </v-col>
    <v-col cols="3">
      <v-text-field
        :label="$t('initiative.edit.radius')"
        density="compact"
        hint="In kilometres"
        suffix="km"
        @change="update"
        v-model.number="address.radius"
        :disabled="disabled"
        v-if="!address.isSpecific"
      />
    </v-col>
  </v-row>
</template>
